import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton,
} from '@mui/material';
import { Refresh, Replay, ContentCopy } from '@mui/icons-material';
import { SharesApi } from '../../services/api/shares';

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'share_viewed', label: 'Views' },
  { value: 'verify_code_requested', label: 'Code Requests' },
  { value: 'verify_code_validated', label: 'Verifications' },
  { value: 'download_requested', label: 'Download Requests' },
  { value: 'download', label: 'Downloads' },
];

function ShareUsageDashboard({ token, distributionId }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);

  // Filters
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterShareId, setFilterShareId] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterEventType, setFilterEventType] = useState('');

  // Redrive
  const [redriveTaskId, setRedriveTaskId] = useState(null);
  const [redriveLoading, setRedriveLoading] = useState(false);
  const [redriveResult, setRedriveResult] = useState(null);
  const [copiedShareId, setCopiedShareId] = useState(null);
  const [copyAnnouncement, setCopyAnnouncement] = useState('');

  const fetchUsage = useCallback(async (cursor) => {
    if (!token || !distributionId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      if (filterShareId) params.shareId = filterShareId;
      if (filterEmail) params.email = filterEmail;
      if (filterEventType) params.eventType = filterEventType;
      params.limit = rowsPerPage;
      if (cursor) params.cursor = cursor;

      const res = await SharesApi.getDistributionUsage(token, distributionId, params);
      if (res.success) {
        setItems(res.data.items || []);
        setSummary(res.data.summary || null);
        setNextCursor(res.data.nextCursor || null);
      } else {
        setError(res.data?.message || 'Failed to load usage data');
      }
    } catch (err) {
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [token, distributionId, filterFrom, filterTo, filterShareId, filterEmail, filterEventType, rowsPerPage]);

  useEffect(() => {
    setPage(0);
    fetchUsage(null);
  }, [fetchUsage]);

  const handleFilter = () => {
    setPage(0);
    fetchUsage(null);
  };

  const handleRedrive = async (taskId) => {
    setRedriveTaskId(taskId);
    setRedriveResult(null);
  };

  const confirmRedrive = async () => {
    if (!redriveTaskId) return;
    setRedriveLoading(true);
    setRedriveResult(null);
    try {
      const res = await SharesApi.redriveDownload(token, redriveTaskId);
      if (res.success) {
        setRedriveResult({ success: true, message: `Redrive queued (task: ${res.data.taskId})` });
      } else {
        setRedriveResult({ success: false, message: res.data?.message || 'Redrive failed' });
      }
    } catch (err) {
      setRedriveResult({ success: false, message: 'Redrive failed' });
    } finally {
      setRedriveLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(Number(ts));
      return d.toLocaleString();
    } catch {
      return ts;
    }
  };

  const handleCopyShareId = async (shareId) => {
    if (!shareId) return;
    try {
      await navigator.clipboard.writeText(shareId);
      setCopiedShareId(shareId);
      setCopyAnnouncement(`Share ID ${shareId.slice(0, 8)} copied to clipboard`);
      setTimeout(() => {
        setCopiedShareId(null);
        setCopyAnnouncement('');
      }, 1500);
    } catch {
      setCopyAnnouncement('Copy failed');
      setTimeout(() => setCopyAnnouncement(''), 1500);
    }
  };

  const formatEventType = (et) => {
    const opt = EVENT_TYPE_OPTIONS.find(o => o.value === et);
    return opt ? opt.label : et;
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
        Share Usage Dashboard
      </Typography>

      {/* Filter form */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Share ID"
          size="small"
          value={filterShareId}
          onChange={(e) => setFilterShareId(e.target.value)}
        />
        <TextField
          label="Email"
          size="small"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
        />
        <TextField
          select
          label="Event Type"
          size="small"
          value={filterEventType}
          onChange={(e) => setFilterEventType(e.target.value)}
          SelectProps={{ native: true }}
        >
          {EVENT_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleFilter} disabled={loading}>
          Filter
        </Button>
        <Button variant="outlined" onClick={() => fetchUsage(null)} disabled={loading} startIcon={<Refresh />}>
          Refresh
        </Button>
      </Box>

      {/* Summary chips */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`Views: ${summary.views ?? 0}`} color="info" variant="outlined" />
          <Chip label={`Verifications: ${summary.verifications ?? 0}`} color="info" variant="outlined" />
          <Chip label={`Downloads: ${summary.downloads ?? 0}`} color="success" variant="outlined" />
          <Chip label={`Errors: ${summary.errors ?? 0}`} color="error" variant="outlined" />
          <Chip label={`Recipients: ${summary.recipients ?? 0}`} color="default" variant="outlined" />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} aria-live="polite"
          action={
            <Button color="inherit" size="small" onClick={() => fetchUsage(null)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">Loading usage data…</Typography>
        </Box>
      ) : items.length === 0 && !error ? (
        <Alert severity="info">No usage events found for this distribution.</Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Share ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.taskId || item.shareId || idx}>
                    <TableCell>{formatTimestamp(item.createdAt)}</TableCell>
                    <TableCell>{formatEventType(item.eventType)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {item.shareId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={item.shareId} arrow>
                            <span>{`${item.shareId.slice(0, 8)}…`}</span>
                          </Tooltip>
                          <Tooltip title={copiedShareId === item.shareId ? 'Copied!' : 'Copy Share ID'} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyShareId(item.shareId)}
                              aria-label="Copy Share ID"
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{item.email || '—'}</TableCell>
                    <TableCell>{item.ip || '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={item.device || ''} arrow>
                        <Typography variant="caption" sx={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.device ? `${item.device.slice(0, 40)}…` : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {item.errorMessage && (
                        <Typography variant="caption" color="error" display="block">
                          {item.errorMessage}
                        </Typography>
                      )}
                      {item.status && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Status: {item.status}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {item.eventType === 'download' && item.status === 'ERROR' && item.taskId && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Replay />}
                          onClick={() => handleRedrive(item.taskId)}
                        >
                          Redrive
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {nextCursor && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => fetchUsage(nextCursor)}
                disabled={loading}
              >
                Load More
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Redrive confirmation dialog */}
      <Dialog open={!!redriveTaskId} onClose={() => setRedriveTaskId(null)}>
        <DialogTitle>Redrive Download</DialogTitle>
        <DialogContent>
          {redriveResult ? (
            <Alert severity={redriveResult.success ? 'success' : 'error'}>
              {redriveResult.message}
            </Alert>
          ) : (
            <Typography>
              Are you sure you want to redrive download task <strong>{redriveTaskId}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedriveTaskId(null)} disabled={redriveLoading}>
            {redriveResult ? 'Close' : 'Cancel'}
          </Button>
          {!redriveResult && (
            <Button onClick={confirmRedrive} variant="contained" disabled={redriveLoading} color="warning"
              aria-label={redriveLoading ? 'Confirming redrive' : 'Confirm Redrive'}>
              {redriveLoading ? <CircularProgress size={20} aria-hidden="true" /> : 'Confirm Redrive'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <span aria-live="polite" aria-atomic="true" className="sr-only" style={{ position: 'absolute', left: -9999 }}>
        {copyAnnouncement}
      </span>
    </Paper>
  );
}

export default ShareUsageDashboard;
